import { Check, Copy } from 'lucide-react';
import type React from 'react';
import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FieldHelpTooltip } from '@/Domain/components/Integration/components/FieldHelpTooltip';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { FieldDescription } from '@/Domain/components/Shared/Field/Field';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Label } from '@/Domain/components/Shared/Label/Label';

const COPY_RESET_MS = 2000;

interface CopyFieldProps {
  label: string;
  value: string;
  testId: string;
  copyTestId: string;
  disabled?: boolean;
  /** Shown in a tooltip behind a help icon next to the label. */
  tooltip?: string;
  /** Shown between the label and the value, in the standard field description style. */
  description?: React.ReactNode;
  /** Called after the value reaches the clipboard, for analytics. */
  onCopy?: () => void;
}

/**
 * A read-only value with a copy button (scenario IDs, tokens, tags, config
 * snippets). Promoted from the Drupal sheet so every integration that vends a
 * value to paste elsewhere uses the same control.
 */
export const CopyField = ({
  label,
  value,
  testId,
  copyTestId,
  disabled = false,
  tooltip,
  description,
  onCopy,
}: CopyFieldProps) => {
  const { t } = useTranslation('integrations');
  const [copied, setCopied] = useState(false);
  // Pair the sibling label and input for assistive technology.
  const inputId = useId();

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      onCopy?.();
      toast.success(t('integrations:copied'));
      setTimeout(() => setCopied(false), COPY_RESET_MS);
    } catch {
      toast.error(t('integrations:copy_failed'));
    }
  }, [value, t, onCopy]);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <Label htmlFor={inputId} className="text-sm font-semibold text-foreground">
          {label}
        </Label>
        {tooltip ? <FieldHelpTooltip content={tooltip} data-testid={`${testId}-help`} /> : null}
      </div>
      {description ? (
        <FieldDescription className="pb-0" data-testid={`${testId}-description`}>
          {description}
        </FieldDescription>
      ) : null}
      <div className="flex items-center gap-2">
        <Input
          id={inputId}
          type="text"
          size="sm"
          value={value}
          readOnly
          disabled={disabled}
          className="flex-1 !bg-muted font-mono text-sm"
          data-testid={testId}
        />
        <Button
          variant="secondary"
          size="sm"
          onClick={handleCopy}
          disabled={disabled}
          data-testid={copyTestId}
          className="whitespace-nowrap"
        >
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? t('integrations:copied') : t('integrations:copy')}
        </Button>
      </div>
    </div>
  );
};

export default CopyField;
